import React, { useCallback, useState } from 'react'
import { OtpInput } from 'reactjs-otp-input'

import { t } from 'src/translations'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'

import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'

const CONFIRMATION_CODE_LENGTH = 6

export type onCompleteProps = {
  identity: string
  code: string
  setError: (err: string) => void
}

type ConfirmCodeProps = {
  identity: string
  onComplete: ({ identity, code, setError }: onCompleteProps) => Promise<any>
  sendCode?: () => void
  loading?: boolean
}

export const ConfirmCodeForm = ({
  identity,
  onComplete,
  sendCode,
  loading
}: ConfirmCodeProps) => {
  return (
    <div className='codeForm' role='form'>
      <ConfirmationCodeInputs identity={identity} onComplete={onComplete} />
      {sendCode && (
        <button
          className='resendCodeButton'
          onClick={sendCode}
          data-testid='resend-code-button'
        >
          {loading ? (
            <LoadingSpinner size='24' strokeWidth='3' color='#252525' />
          ) : (
            t('confirmation-code.resend-code')
          )}
        </button>
      )}
    </div>
  )
}

type ModalProps = {
  onComplete: ({ identity, code, setError }: onCompleteProps) => Promise<any>
  identity: string
  sendCode?: () => void
  isOpen: boolean
  onCancel: () => void
}

export const VerifyEmailConfirmationCodeModal = ({
  onComplete,
  identity,
  sendCode,
  isOpen,
  onCancel
}: ModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent
        contentClassName='modalContentV2 fullScreenMobile authModal'
        data-testid='CodeConfirmationModal'
        ariaLabelledBy='code-confirmation-modal-title'
      >
        <div className='body'>
          <div className='headerContent centered'>
            <h2
              className='title'
              id='code-confirmation-modal-title'
              tabIndex={-1}
            >
              {t('confirmation-code.enter-verification')}
            </h2>
            <p className='subtitle'>
              {t('confirmation-code.code-sent-to', { identity })}
            </p>
          </div>
          <div className='form'>
            <ConfirmCodeForm
              identity={identity}
              onComplete={onComplete}
              sendCode={sendCode}
            />
          </div>
          <button className='cancelVerifyEmailButton' onClick={onCancel}>
            {t('confirmation-code.cancel')}
          </button>
        </div>
      </ModalContent>
    </Modal>
  )
}

interface ConfirmationCodeInputsProps {
  identity: string
  onComplete: ({ identity, code, setError }: onCompleteProps) => Promise<any>
}

export function ConfirmationCodeInputs({
  identity,
  onComplete
}: ConfirmationCodeInputsProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [validatingCode, setValidatingCode] = useState(false)
  // Reset key to force re-render of OTP input when code is incorrect (resets focus)
  const [reset, setReset] = useState(0)

  const onChangeCode = useCallback(
    async (code: string) => {
      setCode(code)

      if (code.length === CONFIRMATION_CODE_LENGTH) {
        try {
          setValidatingCode(true)
          await onComplete({ identity, code, setError })
        } catch (e) {
          setError(t('confirmation-code.error-occurred'))
        } finally {
          setCode('')
          setValidatingCode(false)
          setReset((key) => key + 1)
        }
      }
    },
    [identity, onComplete, setCode]
  )
  return (
    <>
      <OtpInput
        key={reset}
        shouldAutoFocus={true}
        isInputNum={true}
        separator={<span className='separator' />}
        isDisabled={validatingCode}
        containerStyle='inputField inputFieldOtp'
        inputStyle='inputContainer'
        value={code}
        numInputs={CONFIRMATION_CODE_LENGTH}
        onChange={onChangeCode}
      />
      {error && <span className='formError'>{error}</span>}
      {validatingCode && <LoadingSpinnerOverlay />}
    </>
  )
}
